import { supabase } from "./supabase";

interface CampaignRow {
  id: string;
  name: string;
  status: string;
  campaign_type: string | null;
  created_at: string;
}

interface FlightRow {
  id: string;
  campaign_id: string;
  name: string;
  status: string;
  list_id: string | null;
  eib_launch_id: string | null;
}

interface ListRow {
  id: string;
  name: string;
  is_dynamic: boolean;
  list_type: string | null;
  build_status: string | null;
  contact_count: number | null;
  metadata: unknown;
}

interface SnapshotSource {
  id: string;
  name: string;
  is_dynamic: boolean;
  contact_count: number | null;
  role: "include" | "exclude";
}

export interface EventCampaignAudienceSegment {
  id: string;
  name: string;
  isDynamic: boolean;
  /** Current cached size of the source segment, when it still exists. */
  currentCount: number | null;
  /** Membership actually frozen for the invitation flight. */
  frozenCount: number | null;
  sourceAvailable: boolean;
}

export interface EventCampaignWithAudience {
  id: string;
  name: string;
  status: string;
  campaignType: string | null;
  createdAt: string;
  isEib: boolean;
  flightCount: number;
  audienceSegments: EventCampaignAudienceSegment[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Combined EIB invitation lists are immutable send snapshots. create_combined_list
 * records the operator-facing source segment(s) in metadata.sources, which lets the
 * event page show the segment the operator selected/created without pretending the
 * internal frozen snapshot is an editable segment.
 */
export function readSnapshotSources(metadata: unknown): SnapshotSource[] {
  const record = asRecord(metadata);
  if (!record || !Array.isArray(record.sources)) {
    return [];
  }

  const sources: SnapshotSource[] = [];
  for (const raw of record.sources) {
    const source = asRecord(raw);
    if (
      !source ||
      typeof source.id !== "string" ||
      typeof source.name !== "string" ||
      (source.role !== "include" && source.role !== "exclude")
    ) {
      continue;
    }
    sources.push({
      id: source.id,
      name: source.name,
      is_dynamic: source.is_dynamic === true,
      contact_count:
        typeof source.contact_count === "number" ? source.contact_count : null,
      role: source.role,
    });
  }
  return sources;
}

function unique(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => !!value))];
}

export async function getEventCampaignsWithAudiences(
  eventId: string,
): Promise<EventCampaignWithAudience[]> {
  const { data: campaignData, error: campaignError } = await supabase
    .from("campaigns")
    .select("id,name,status,campaign_type,created_at")
    .eq("event_id", eventId)
    .not("is_deleted", "is", true) // soft-deleted campaigns are hidden (migration 203)
    .order("created_at", { ascending: false });

  if (campaignError) {
    throw campaignError;
  }

  const campaigns = (campaignData ?? []) as CampaignRow[];
  if (campaigns.length === 0) {
    return [];
  }

  const campaignIds = campaigns.map((campaign) => campaign.id);
  const { data: flightData, error: flightError } = await supabase
    .from("campaign_flights")
    .select("id,campaign_id,name,status,list_id,eib_launch_id")
    .in("campaign_id", campaignIds);

  if (flightError) {
    throw flightError;
  }

  const flights = (flightData ?? []) as FlightRow[];
  const targetListIds = unique(flights.map((flight) => flight.list_id));
  let targetLists: ListRow[] = [];
  if (targetListIds.length > 0) {
    const { data, error } = await supabase
      .from("lists")
      .select(
        "id,name,is_dynamic,list_type,build_status,contact_count,metadata",
      )
      .in("id", targetListIds);
    if (error) {
      throw error;
    }
    targetLists = (data ?? []) as ListRow[];
  }

  const targetById = new Map(targetLists.map((list) => [list.id, list]));
  const provenance = new Map<string, SnapshotSource>();
  for (const list of targetLists) {
    for (const source of readSnapshotSources(list.metadata)) {
      if (source.role === "include") {
        provenance.set(source.id, source);
      }
    }
  }

  // Fetch live source rows when available. Snapshot provenance remains the
  // fallback if a source was later renamed or deleted.
  const sourceIds = [...provenance.keys()];
  let liveSources: ListRow[] = [];
  if (sourceIds.length > 0) {
    const { data, error } = await supabase
      .from("lists")
      .select(
        "id,name,is_dynamic,list_type,build_status,contact_count,metadata",
      )
      .in("id", sourceIds);
    if (error) {
      throw error;
    }
    liveSources = (data ?? []) as ListRow[];
  }
  const liveSourceById = new Map(liveSources.map((list) => [list.id, list]));

  return campaigns.map((campaign) => {
    const campaignFlights = flights.filter(
      (flight) => flight.campaign_id === campaign.id,
    );
    const segments = new Map<string, EventCampaignAudienceSegment>();

    for (const flight of campaignFlights) {
      if (!flight.list_id) {
        continue;
      }
      const target = targetById.get(flight.list_id);
      const includedSources = target
        ? readSnapshotSources(target.metadata).filter(
            (source) => source.role === "include",
          )
        : [];

      if (includedSources.length > 0) {
        for (const source of includedSources) {
          const live = liveSourceById.get(source.id);
          segments.set(source.id, {
            id: source.id,
            name: live?.name ?? source.name,
            isDynamic: live?.is_dynamic ?? source.is_dynamic,
            currentCount: live?.contact_count ?? source.contact_count,
            frozenCount: target?.contact_count ?? null,
            sourceAvailable: !!live,
          });
        }
      } else if (target) {
        // Non-EIB/manual event campaign: its flight list is itself the audience
        // segment, so it is still useful to show it in the event Campaigns tab.
        segments.set(target.id, {
          id: target.id,
          name: target.name,
          isDynamic: target.is_dynamic,
          currentCount: target.contact_count,
          frozenCount: target.contact_count,
          sourceAvailable: true,
        });
      }
    }

    return {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      campaignType: campaign.campaign_type,
      createdAt: campaign.created_at,
      isEib: campaignFlights.some((flight) => !!flight.eib_launch_id),
      flightCount: campaignFlights.length,
      audienceSegments: [...segments.values()],
    };
  });
}
